import { useEffect, useRef, useState } from "react";
import { Cake, Heart, Sparkles, Volume2, VolumeX, Dog } from "lucide-react";
import {
  computeBirthdayContext,
  bdayAudioAlreadyPlayed,
  markBdayAudioPlayed,
  formatDay,
} from "@/lib/birthdays";

const WORKER_BASE = (
  process.env.REACT_APP_PAPI_WORKER_URL
  || "https://preguntale-a-papi.guendelman.workers.dev"
).replace(/\/ask-papi\/?$/, "").replace(/\/+$/, "");

// Textos con voz PVC del Pollo — se envían a /guest-tts pa' generar audio
// on-demand la primera vez que el cumpleañero entra ese día.
function buildBirthdayText(name, age) {
  const edad = age && age > 0 && age < 100 ? ` Estás cumpliendo ${age} años. ` : " ";
  return `¡Feliz cumpleaños, ${name}! Hoy es un día importante en tu vida y quería saludarte con todo mi cariño de papá.${edad}¿Qué vamos a cocinar juntos para celebrar este día tan importante?`;
}

function buildAnniversaryText(years) {
  const yrs = years && years > 0 && years < 100 ? ` ${years} años juntos. ` : " ";
  return `¡Feliz aniversario, mi amor!${yrs}Gracias por la familia que armamos, y por seguir cocinando la vida a mi lado.`;
}

export default function BirthdayBanner({ member, displayName }) {
  const [ctx, setCtx] = useState(null);
  const [speaking, setSpeaking] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!member) return;
    let cancelled = false;
    computeBirthdayContext(member).then((c) => {
      if (!cancelled) setCtx(c);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [member]);

  // Auto-play del saludo la PRIMERA vez del día pa' el cumpleañero.
  useEffect(() => {
    if (!ctx) return;
    const target = ctx.selfToday || ctx.anniversaryToday;
    if (!target) return;
    if (bdayAudioAlreadyPlayed(member, ctx.today)) return;
    // Marca ANTES de intentar reproducir (aunque autoplay falle, no queremos
    // que se dispare varias veces si el user recarga rápido)
    markBdayAudioPlayed(member, ctx.today);
    const t = setTimeout(() => {
      playSalute(target);
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx]);

  const stop = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setSpeaking(false);
  };

  const playSalute = async (target) => {
    if (speaking) { stop(); return; }
    const name = displayName || target.name;
    const text = target.type === "anniversary"
      ? buildAnniversaryText(target.turning_age)
      : buildBirthdayText(name, target.turning_age);
    setSpeaking(true);
    try {
      const res = await fetch(`${WORKER_BASE}/guest-tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error("tts_failed");
      const data = await res.json();
      if (!data.audio_url) throw new Error("no_audio");
      const audio = new Audio(data.audio_url);
      audioRef.current = audio;
      audio.onended = () => { setSpeaking(false); audioRef.current = null; };
      audio.onerror = () => { setSpeaking(false); audioRef.current = null; };
      await audio.play().catch(() => { setSpeaking(false); audioRef.current = null; });
    } catch {
      setSpeaking(false);
    }
  };

  if (!ctx) return null;

  const { selfToday, anniversaryToday, othersToday, upcoming } = ctx;

  // Caso 1: TU cumpleaños hoy (banner grande + auto-play)
  if (selfToday) {
    const name = displayName || selfToday.name;
    return (
      <div
        data-testid="birthday-self-banner"
        className="mb-6 relative overflow-hidden rounded-2xl border-2 border-amber-deep bg-gradient-to-br from-amber-light/60 via-cream to-paper-warm p-6 sm:p-8 shadow-[0_20px_40px_-18px_rgba(168,93,38,0.4)]"
      >
        <Sparkles className="absolute -top-3 -right-3 w-24 h-24 text-amber-deep/10 rotate-12" />
        <div className="flex items-center gap-2 mb-2">
          <Cake className="w-5 h-5 text-amber-deep" />
          <p className="eyebrow text-amber-deep">Hoy es tu día</p>
        </div>
        <h2 className="font-display italic text-cocoa text-4xl sm:text-5xl leading-tight mb-3">
          ¡Feliz cumpleaños,<br /><span className="text-amber-deep">{name}</span>!
        </h2>
        <p className="font-serif text-cocoa text-lg leading-relaxed mb-5">
          Un día importante en tu vida — y quería saludarte con todo mi cariño de papá.
          ¿<b>Qué vamos a cocinar juntos</b> para celebrar este día tan especial?
        </p>
        <button
          data-testid="birthday-play-btn"
          onClick={() => selfToday && (speaking ? stop() : playSalute(selfToday))}
          className={`inline-flex items-center gap-2 px-5 py-3 rounded-full font-serif font-semibold transition ${speaking ? "bg-amber-deep text-cream" : "bg-cocoa text-cream hover:bg-amber-deep"}`}
        >
          {speaking ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          {speaking ? "Detener" : "Escuchar el saludo de Papi"}
        </button>
      </div>
    );
  }

  // Caso 2: Aniversario (Papi & Mamá)
  if (anniversaryToday) {
    return (
      <div
        data-testid="birthday-anniversary-banner"
        className="mb-6 relative overflow-hidden rounded-2xl border-2 border-amber-deep bg-gradient-to-br from-amber-light/60 via-cream to-paper-warm p-6 sm:p-8 shadow-[0_20px_40px_-18px_rgba(168,93,38,0.4)]"
      >
        <Heart className="absolute -top-3 -right-3 w-24 h-24 text-amber-deep/10 rotate-12" />
        <div className="flex items-center gap-2 mb-2">
          <Heart className="w-5 h-5 text-amber-deep" />
          <p className="eyebrow text-amber-deep">Hoy es su aniversario</p>
        </div>
        <h2 className="font-display italic text-cocoa text-4xl sm:text-5xl leading-tight mb-3">
          ¡Feliz aniversario,<br /><span className="text-amber-deep">mi amor</span>!
        </h2>
        <p className="font-serif text-cocoa text-lg leading-relaxed mb-5">
          {anniversaryToday.turning_age} años juntos. Gracias por la familia que armamos,
          y por seguir cocinando la vida a mi lado.
        </p>
        <button
          data-testid="anniversary-play-btn"
          onClick={() => speaking ? stop() : playSalute(anniversaryToday)}
          className={`inline-flex items-center gap-2 px-5 py-3 rounded-full font-serif font-semibold transition ${speaking ? "bg-amber-deep text-cream" : "bg-cocoa text-cream hover:bg-amber-deep"}`}
        >
          {speaking ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          {speaking ? "Detener" : "Escuchar el saludo de Papi"}
        </button>
      </div>
    );
  }

  // Caso 3: cumple de otro (familia amplia) — banner chico, sin audio
  const notes = [];
  for (const p of othersToday) {
    if (p.is_pet) {
      notes.push({
        key: `pet-${p.name}`,
        icon: <Dog className="w-4 h-4" />,
        text: `Hoy cumple nuestro ${p.name} 🐶. ¡Denle un regaloneo (y una migaja rica)!`,
        pet: true,
      });
    } else {
      notes.push({
        key: `today-${p.name}`,
        icon: <Cake className="w-4 h-4" />,
        text: `Hoy cumple ${p.name}${p.turning_age && p.turning_age < 100 ? ` (${p.turning_age})` : ""}. Anda a mandarle un abrazo.`,
        pet: false,
      });
    }
  }
  for (const p of upcoming) {
    const label = p.days_until === 1 ? "mañana" : `en ${p.days_until} días`;
    notes.push({
      key: `up-${p.name}`,
      icon: <Sparkles className="w-4 h-4" />,
      text: `Se viene el cumple de ${p.name} ${label} (${formatDay(p.bd)}). Vayamos pensando qué cocinar.`,
      pet: false,
    });
  }

  if (!notes.length) return null;

  return (
    <div data-testid="birthday-family-notes" className="mb-6 space-y-2">
      {notes.map((n) => (
        <div
          key={n.key}
          data-testid={`birthday-note-${n.key}`}
          className={`flex items-start gap-3 p-4 rounded-lg border ${n.pet ? "bg-amber-light/20 border-amber-deep/40" : "bg-cream border-line"}`}
        >
          <span className="mt-0.5 text-amber-deep">{n.icon}</span>
          <p className="font-serif text-cocoa text-base leading-snug">{n.text}</p>
        </div>
      ))}
    </div>
  );
}
